import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';

const variants = {
    success: { bg: 'bg-emerald-600', icon: CheckCircle, label: 'Success' },
    error: { bg: 'bg-red-600', icon: AlertCircle, label: 'Error' },
    warning: { bg: 'bg-amber-600', icon: AlertTriangle, label: 'Warning' },
    info: { bg: 'bg-blue-600', icon: Info, label: 'Info' },
};

let toastIdCounter = 0;

const toasts = {
    _fn: null,
    register(fn) {
        this._fn = fn;
    },
    unregister() {
        this._fn = null;
    },
    show(message, opts = {}) {
        if (this._fn) this._fn({ id: ++toastIdCounter, message, ...opts });
    },
    success(message, opts) {
        this.show(message, { variant: 'success', ...opts });
    },
    error(message, opts) {
        this.show(message, { variant: 'error', ...opts });
    },
    warning(message, opts) {
        this.show(message, { variant: 'warning', ...opts });
    },
    info(message, opts) {
        this.show(message, { variant: 'info', ...opts });
    },
};

export const toast = toasts;

export const ToastContainer = () => {
    const [items, setItems] = useState([]);
    const timersRef = useRef({});

    const remove = useCallback((id) => {
        setItems((prev) => prev.filter((t) => t.id !== id));
        if (timersRef.current[id]) {
            clearTimeout(timersRef.current[id]);
            delete timersRef.current[id];
        }
    }, []);

    useEffect(() => {
        toasts.register((item) => {
            const id = item.id;
            setItems((prev) => [...prev, { ...item, variant: item.variant || 'info' }]);
            const duration = item.duration || 4000;
            timersRef.current[id] = setTimeout(() => remove(id), duration);
        });
        return () => {
            toasts.unregister();
        };
    }, [remove]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, []);

    const hasError = items.some(item => item.variant === 'error');
    return createPortal(
        <div
            className="fixed bottom-0 right-0 left-0 sm:bottom-6 sm:right-6 sm:left-auto z-[9999] flex flex-col gap-3 px-4 sm:px-0 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-0 pointer-events-none"
            aria-live={hasError ? 'assertive' : 'polite'}>
            <AnimatePresence>
                {items.map((item) => {
                    const cfg = variants[item.variant] || variants.info;
                    const Icon = cfg.icon;
                    return (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 24, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 16, scale: 0.95 }}
                            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                            className={`${cfg.bg} pointer-events-auto flex items-start gap-3 rounded-2xl sm:rounded-[20px] px-4 sm:px-5 py-3 sm:py-4 text-sm font-medium text-white shadow-[0_12px_40px_rgba(0,0,0,0.25)] w-full sm:max-w-sm`}
                        >
                            <Icon size={18} className="shrink-0 mt-0.5" />
                            <span className="flex-1 leading-5 sm:leading-6">{item.message}</span>
                            <button
                                type="button"
                                onClick={() => remove(item.id)}
                                className="shrink-0 opacity-70 hover:opacity-100 transition"
                                aria-label="Dismiss"
                            >
                                <X size={15} />
                            </button>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>,
        document.body,
    );
};
